/**
 * Dashboard — the personal landing page for Care Support Leaders and Facilitators
 * at `/Dashboard` (formerly "Control Center" at `/ControlCenter`).
 *
 * Everything here is scoped to the logged-in user. The org-wide view is `/Overview`,
 * which is admin-only.
 *
 * Role handling: a person can be a leader, a facilitator, or both. Sections are gated on
 * the role claim and each data hook takes the matching `enabled` flag, so a facilitator
 * never fires a leader request and vice versa — the fix for the old ControlCenter
 * behaviour of calling both portals for everyone (`_memory/_context/06-known-issues.md` A1).
 * A dual-role user gets both halves stacked under their own headings, with one merged
 * attention band and one merged session list on top.
 *
 * Sessions-today/this-week are shown per role rather than summed. The old page added them
 * across both stats objects, which produced a number that meant nothing when only one of
 * the two roles was real.
 */

import { useMemo } from 'react';
import {
  ActionIcon, Alert, Divider, Group, SegmentedControl, SimpleGrid, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle, IconCalendarEvent, IconCalendarWeek, IconChecklist,
  IconClockExclamation, IconEyeOff, IconFileAlert, IconFolders, IconInbox,
  IconInfoCircle, IconRefresh, IconUsers,
} from '@tabler/icons-react';
import { Stat } from '@/components';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { formatSDKDate } from '@/utils/dateTime';
import ActionBand, { type ActionItem } from './Components/ActionBand';
import MyListCard, { type MyListRow } from './Components/MyListCard';
import MySessionsList, {
  normaliseFacilitatorSession,
  normaliseLeaderSession,
  type NormalisedSession,
} from './Components/MySessionsList';
import useFacilitatorDashboard from './Hooks/useFacilitatorDashboard';
import useLeaderDashboard from './Hooks/useLeaderDashboard';
import useMyRoles, { type DashboardView } from './Hooks/useMyRoles';

export default function DashboardPage() {
  const { getUserDisplayName } = useAuth();
  const {
    isBoth, isNeither, view, setView, showLeader, showFacilitator, showBoth,
  } = useMyRoles();

  // `showLeader` / `showFacilitator`, not the raw role claims — narrowing the view also stops
  // the other half's requests rather than fetching data the page won't render.
  const leader = useLeaderDashboard(showLeader);
  const facilitator = useFacilitatorDashboard(showFacilitator);

  const loading = leader.loading || facilitator.loading;

  const handleRefresh = () => {
    leader.refresh();
    facilitator.refresh();
  };

  // ── Attention band ────────────────────────────────────────────────────────
  // Merged across roles and ordered by urgency, not by role. Zero-count items are
  // dropped inside ActionBand, and items whose data source doesn't exist yet are
  // omitted here rather than shown as a misleading zero.
  const actionItems = useMemo<ActionItem[]>(() => {
    const items: ActionItem[] = [];

    if (showLeader) {
      items.push({
        key: 'pending-cases',
        label: 'case invitations to answer',
        count: leader.pendingCases.length,
        icon: IconInbox,
        color: 'grape',
        to: '/Counselling/PendingCases',
        hint: 'Accept or decline so the case can move',
      });

      if (leader.unviewedCases !== null) {
        items.push({
          key: 'unviewed-cases',
          label: 'cases you have never opened',
          count: leader.unviewedCases.length,
          icon: IconEyeOff,
          color: 'red',
          to: '/Counselling/MyCases',
        });
      }

      items.push({
        key: 'stale-cases',
        label: 'cases with no recent activity',
        count: leader.staleCases.length,
        icon: IconClockExclamation,
        color: 'orange',
        to: '/Counselling/MyCases',
        hint: leader.staleCases.length
          ? `Oldest: ${leader.staleCases[0].daysSinceLastActivity} days quiet`
          : undefined,
      });
    }

    if (showFacilitator) {
      items.push({
        key: 'overdue-sessions',
        label: 'sessions past their date',
        count: facilitator.stats?.myOverdueSessions ?? facilitator.overdueSessions.length,
        icon: IconAlertTriangle,
        color: 'red',
        to: '/Grow/Enrolment/ActiveEnrolments',
        hint: 'Mark them complete or reschedule',
      });

      items.push({
        key: 'behind-schedule',
        label: 'registrations behind schedule',
        count: facilitator.behindScheduleRegistrations.length,
        icon: IconClockExclamation,
        color: 'orange',
        to: '/Grow/Enrolment',
      });

      if (facilitator.incompleteForms !== null) {
        items.push({
          key: 'incomplete-forms',
          label: 'session forms to complete',
          count: facilitator.incompleteForms.length,
          icon: IconFileAlert,
          color: 'yellow',
          to: '/Grow/Enrolment',
        });
      }
    }

    // Today's sessions last — informational rather than a backlog.
    const todayCount = leader.sessionsToday.length + facilitator.sessionsToday.length;
    items.push({
      key: 'sessions-today',
      label: 'sessions today',
      count: todayCount,
      icon: IconCalendarEvent,
      color: 'green',
    });

    return items;
  }, [showLeader, showFacilitator, leader, facilitator]);

  // ── Merged session list ───────────────────────────────────────────────────
  const allSessions = useMemo<NormalisedSession[]>(() => [
    ...leader.sessions.map(normaliseLeaderSession),
    ...facilitator.sessions.map(normaliseFacilitatorSession),
  ], [leader.sessions, facilitator.sessions]);

  // ── Row mapping ───────────────────────────────────────────────────────────
  const myCaseRows = useMemo<MyListRow[]>(
    () => [...leader.cases]
      .sort((a, b) => (b.daysSinceLastActivity ?? 0) - (a.daysSinceLastActivity ?? 0))
      .map((c) => ({
        id: c.caseID,
        title: c.name ?? `Case #${c.caseID}`,
        meta: [c.needName, c.counseleeNames, `Opened ${formatSDKDate(c.caseDateTime)}`]
          .filter(Boolean).join(' · '),
        badge: c.statusName ?? undefined,
        badgeColor: c.statusColor,
        to: `/Counselling/MyCases/${c.caseID}`,
      })),
    [leader.cases],
  );

  const pendingCaseRows = useMemo<MyListRow[]>(
    () => leader.pendingCases.map((c) => ({
      id: c.caseID,
      title: c.name ?? `Case #${c.caseID}`,
      meta: [c.needName, c.counseleeNames].filter(Boolean).join(' · ') || undefined,
      badge: 'Awaiting you',
      badgeColor: c.needColor,
      to: '/Counselling/PendingCases',
    })),
    [leader.pendingCases],
  );

  const myRegistrationRows = useMemo<MyListRow[]>(
    () => [...facilitator.registrations]
      .sort((a, b) => (a.progressPercent ?? 0) - (b.progressPercent ?? 0))
      .map((r) => ({
        id: r.registrationID,
        title: r.courseName ?? r.reference ?? `Registration #${r.registrationID}`,
        meta: [
          r.periodDisplay,
          r.isRemote ? 'Remote' : r.locationName,
          `${r.participantCount} participants`,
          r.isLead ? 'Lead' : undefined,
        ].filter(Boolean).join(' · '),
        badge: r.statusName ?? undefined,
        badgeColor: r.statusColor,
        progress: r.progressPercent ?? 0,
        to: `/Grow/Enrolment/RegistrationDetails/${r.registrationID}`,
      })),
    [facilitator.registrations],
  );

  const overdueSessionRows = useMemo<MyListRow[]>(
    () => facilitator.overdueSessions.map((s) => ({
      id: s.registrationSessionID,
      title: s.name ?? 'Session',
      meta: [s.courseName, s.sessionDateDisplay].filter(Boolean).join(' · ') || undefined,
      badge: s.progressStatusName ?? s.statusName ?? undefined,
      badgeColor: s.progressStatusColor ?? s.statusColor,
      to: `/Grow/Enrolment/RegistrationDetails/${s.registrationID}`,
    })),
    [facilitator.overdueSessions],
  );

  // Describes what is actually on screen, which for a dual-role user depends on the toggle
  // rather than on the role claims alone.
  const subtitle = isNeither
    ? 'Your personal dashboard.'
    : showBoth
      ? 'Your cases and your courses, and what needs you next.'
      : showLeader
        ? 'Your cases, sessions and what needs you next.'
        : 'Your registrations, sessions and what needs you next.';

  return (
    <Stack gap="xl">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>Welcome back, {getUserDisplayName()}</Title>
          <Text c="dimmed" size="sm">{subtitle}</Text>
        </div>

        <Group gap="sm" wrap="nowrap">
          {/*
            Only a dual-role user sees this. Someone who is both a leader and a facilitator gets a
            long page, and in practice wears one hat at a time — narrowing also stops the other
            half's requests. The choice persists (`usePageFilters`), so returning to the Dashboard
            lands where you left off.
          */}
          {isBoth && (
            <SegmentedControl
              size="sm"
              value={view}
              onChange={(value) => setView(value as DashboardView)}
              data={[
                { value: 'all', label: 'All' },
                { value: 'leader', label: 'As Care Leader' },
                { value: 'facilitator', label: 'As Facilitator' },
              ]}
            />
          )}

          <Tooltip label="Refresh">
            <ActionIcon variant="light" size="lg" onClick={handleRefresh} loading={loading}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {isNeither ? (
        <Alert icon={<IconInfoCircle size={18} />} title="Nothing assigned to you yet" color="blue">
          This page shows your own cases and course registrations. You are not currently set up as a
          Care Support Leader or a Facilitator, so there is nothing to show. Speak to the care office
          if you think that is wrong.
        </Alert>
      ) : (
        <>
          {/* ── Needs your attention ───────────────────────────────────────── */}
          <Stack gap="sm">
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Needs your attention</Text>
            <ActionBand items={actionItems} loading={loading} />
          </Stack>

          {/* ── My Care (leader) ───────────────────────────────────────────── */}
          {showLeader && (
            <>
              {showBoth && <Divider label="My Care" labelPosition="left" />}

              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                <Stat
                  label="My Open Cases"
                  value={leader.stats?.myOpenCases ?? 0}
                  icon={<IconFolders size={20} color="var(--mantine-color-indigo-6)" />}
                />
                <Stat
                  label="My Sessions Today"
                  value={leader.stats?.mySessionsToday ?? 0}
                  icon={<IconCalendarEvent size={20} color="var(--mantine-color-green-6)" />}
                />
                <Stat
                  label="My Sessions This Week"
                  value={leader.stats?.mySessionsThisWeek ?? 0}
                  icon={<IconCalendarWeek size={20} color="var(--mantine-color-teal-6)" />}
                />
                <Stat
                  label="My Stale Cases"
                  value={leader.stats?.myStaleCases ?? 0}
                  icon={<IconClockExclamation size={20} color="var(--mantine-color-orange-6)" />}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                <MyListCard
                  title="Cases needing attention"
                  subtitle="Yours, quietest first"
                  rows={myCaseRows}
                  loading={leader.loading}
                  viewAllTo="/Counselling/MyCases"
                  emptyMessage="You have no open cases."
                />
                <MyListCard
                  title="Case invitations"
                  subtitle="Waiting on your accept or decline"
                  rows={pendingCaseRows}
                  loading={leader.loading}
                  viewAllTo="/Counselling/PendingCases"
                  emptyMessage="No invitations waiting."
                />
              </SimpleGrid>
            </>
          )}

          {/* ── My Courses (facilitator) ───────────────────────────────────── */}
          {showFacilitator && (
            <>
              {showBoth && <Divider label="My Courses" labelPosition="left" />}

              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                <Stat
                  label="My Active Registrations"
                  value={facilitator.stats?.myActiveRegistrations ?? 0}
                  icon={<IconChecklist size={20} color="var(--mantine-color-blue-6)" />}
                />
                <Stat
                  label="My Sessions Today"
                  value={facilitator.stats?.mySessionsToday ?? 0}
                  icon={<IconCalendarEvent size={20} color="var(--mantine-color-green-6)" />}
                />
                <Stat
                  label="My Sessions This Week"
                  value={facilitator.stats?.mySessionsThisWeek ?? 0}
                  icon={<IconCalendarWeek size={20} color="var(--mantine-color-teal-6)" />}
                />
                <Stat
                  label="My Overdue Sessions"
                  value={facilitator.stats?.myOverdueSessions ?? 0}
                  icon={<IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />}
                />
                <Stat
                  label="My Participants"
                  value={facilitator.stats?.myParticipantsCount ?? 0}
                  icon={<IconUsers size={20} color="var(--mantine-color-grape-6)" />}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                <MyListCard
                  title="My registrations"
                  subtitle="Least progressed first"
                  rows={myRegistrationRows}
                  loading={facilitator.loading}
                  viewAllTo="/Grow/Enrolment"
                  emptyMessage="You are not assigned to any registrations."
                />
                <MyListCard
                  title="Overdue sessions"
                  subtitle="Past their date and not marked complete"
                  rows={overdueSessionRows}
                  loading={facilitator.loading}
                  viewAllTo="/Grow/Enrolment/ActiveEnrolments"
                  emptyMessage="No overdue sessions."
                />
              </SimpleGrid>
            </>
          )}

          {/* ── Merged schedule ────────────────────────────────────────────── */}
          <MySessionsList
            sessions={allSessions}
            loading={loading}
            subtitle={
              showBoth
                ? 'Across your cases and courses'
                : showLeader ? 'Across your cases' : 'Across your courses'
            }
          />
        </>
      )}
    </Stack>
  );
}
